import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, UserPlus, Check, X, Trash2, Ban, ArrowLeft, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { onPersonalEvent } from "../hooks/usePersonalSocket";
import { countryFlag } from "../lib/derive";
import { countryName } from "../lib/countries";
import { TierChip } from "./Leaderboards";
import { UserName } from "../components/UserName";
import Avatar from "../components/Avatar";

const TABS = [
  { key: "accepted",         label: "Friends" },
  { key: "pending_incoming", label: "Requests" },
  { key: "pending_outgoing", label: "Sent" },
  { key: "blocked",          label: "Blocked" },
];

function FriendRow({ row, onAction }) {
  const u = row.user;
  return (
    <div
      data-testid={`friend-row-${u.user_id}`}
      className="flex items-center justify-between gap-4 px-5 py-4 rounded-xl border border-white/5 bg-zinc-950/70 hover:border-white/10 transition"
    >
      <Link to={`/users/${u.user_id}`} className="flex items-center gap-3 min-w-0">
        <div className="relative shrink-0">
          <Avatar user={u} size="md" />
          {u.is_online && (
            <span className="absolute -bottom-0.5 -left-0.5 size-3 rounded-full bg-emerald-500 ring-2 ring-black" />
          )}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-zinc-100 truncate flex items-center gap-2">
            <UserName user={u} />
            <span className="text-[11px] text-zinc-500">{countryFlag(u.country)} {countryName(u.country)}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {u.is_online ? "online" : "offline"}
            </span>
          </div>
        </div>
      </Link>
      <div className="flex items-center gap-2 shrink-0">{onAction(row)}</div>
    </div>
  );
}

export default function Friends() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("accepted");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [counts, setCounts] = useState({ accepted_count: 0, pending_incoming_count: 0,
                                          pending_outgoing_count: 0, lobby_invites_pending_count: 0 });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [{ data: list }, { data: c }] = await Promise.all([
      api.get(`/friends?status=${tab}`),
      api.get("/friends/counts"),
    ]);
    setItems(list.items || []);
    setCounts(c);
  };
  useEffect(() => { reload(); /* eslint-disable-line */ }, [tab]);

  useEffect(() => onPersonalEvent((evt) => {
    if (["friend_request_received", "friend_request_accepted",
         "friend_request_declined", "friend_status_changed"].includes(evt.type)) {
      reload();
    }
  }), [tab]); // eslint-disable-line

  useEffect(() => {
    const q = search.trim();
    if (!q) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`);
        setSearchResults(data.items || []);
      } catch { /* */ }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const act = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); await reload(); } finally { setBusy(false); }
  };

  const requestFriend  = (uid) => act(() => api.post("/friends/request", { to_user_id: uid }));
  const accept         = (fid) => act(() => api.post(`/friends/${fid}/accept`));
  const decline        = (fid) => act(() => api.post(`/friends/${fid}/decline`));
  const cancel         = (fid) => act(() => api.post(`/friends/${fid}/cancel`));
  const remove         = (fid) => act(() => api.delete(`/friends/${fid}`));
  const block          = (uid) => act(() => api.post(`/friends/${uid}/block`));
  const unblock        = (uid) => act(() => api.post(`/friends/${uid}/unblock`));

  const renderRowActions = (row) => {
    if (tab === "accepted") {
      return (
        <>
          <button data-testid={`btn-remove-${row.user.user_id}`}
                  onClick={() => remove(row.friendship_id)}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs hover:bg-white/10 inline-flex items-center gap-1.5">
            <Trash2 size={12} /> Remove
          </button>
          <button data-testid={`btn-block-${row.user.user_id}`}
                  onClick={() => block(row.user.user_id)}
                  className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300 hover:bg-red-500/20 inline-flex items-center gap-1.5">
            <Ban size={12} /> Block
          </button>
        </>
      );
    }
    if (tab === "pending_incoming") {
      return (
        <>
          <button data-testid={`btn-accept-${row.friendship_id}`}
                  onClick={() => accept(row.friendship_id)}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-xs text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center gap-1.5">
            <Check size={12} /> Accept
          </button>
          <button onClick={() => decline(row.friendship_id)}
                  className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs hover:bg-white/10 inline-flex items-center gap-1.5">
            <X size={12} /> Decline
          </button>
        </>
      );
    }
    if (tab === "pending_outgoing") {
      return (
        <button onClick={() => cancel(row.friendship_id)}
                className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs hover:bg-white/10 inline-flex items-center gap-1.5">
          <X size={12} /> Cancel
        </button>
      );
    }
    return (
      <button onClick={() => unblock(row.user.user_id)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs hover:bg-white/10">
        Unblock
      </button>
    );
  };

  return (
    <div data-testid="friends-page" className="min-h-screen bg-[var(--bg)] text-zinc-200">
      <header className="border-b border-white/5 sticky top-0 bg-[var(--bg)]/95 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-widest opacity-60 hover:opacity-100 inline-flex items-center gap-1.5">
            <ArrowLeft size={14}/> back
          </button>
          <h1 className="text-xs uppercase tracking-[0.4em] inline-flex items-center gap-2">
            <Users size={14} /> Friends
          </h1>
          <span className="w-12" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* search */}
        <section data-testid="friends-search">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Find players</label>
          <div className="mt-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="friends-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username…"
              className="w-full pl-9 pr-4 py-3 rounded-lg bg-zinc-950/80 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {searchResults.slice(0, 10).map((u) => (
                <div key={u.user_id}
                     data-testid={`search-result-${u.user_id}`}
                     className="flex items-center justify-between gap-4 px-4 py-2.5 rounded-lg border border-white/5 bg-zinc-950/70">
                  <Link to={`/users/${u.user_id}`} className="flex items-center gap-3 min-w-0">
                    <Avatar user={u} size="sm" />
                    <div className="min-w-0">
                      <div className="font-bold text-zinc-100 truncate text-sm">
                        <UserName user={u} /> <span className="ml-1 text-[11px] text-zinc-500">{countryFlag(u.country)}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{u.friendship_status.replace("_", " ")}</div>
                    </div>
                  </Link>
                  {u.friendship_status === "none" && (
                    <button onClick={() => requestFriend(u.user_id)}
                            className="px-3 py-1.5 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-xs hover:bg-[var(--accent)]/25 inline-flex items-center gap-1.5">
                      <UserPlus size={12} /> Add
                    </button>
                  )}
                  {u.friendship_status === "pending_outgoing" && <span className="text-[11px] text-zinc-500">Pending</span>}
                  {u.friendship_status === "accepted" && <span className="text-[11px] text-emerald-400">Friends</span>}
                  {u.friendship_status === "blocked" && <span className="text-[11px] text-red-400">Blocked</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* tabs */}
        <section>
          <div data-testid="friends-tabs" className="inline-flex p-1 rounded-lg bg-zinc-950 border border-white/5">
            {TABS.map((t) => {
              const count = t.key === "accepted" ? counts.accepted_count
                          : t.key === "pending_incoming" ? counts.pending_incoming_count
                          : t.key === "pending_outgoing" ? counts.pending_outgoing_count : 0;
              return (
                <button key={t.key}
                        data-testid={`tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-1.5 rounded-md text-xs uppercase tracking-widest transition ${tab === t.key ? "bg-white/10 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}>
                  {t.label}{count > 0 && <span className="ml-1.5 text-[10px] text-[var(--accent)]">{count}</span>}
                </button>
              );
            })}
          </div>

          <div className="mt-4 space-y-2">
            {items.length === 0 && (
              <div className="text-center py-12 text-sm text-zinc-500">No {tab.replace("_", " ")} yet</div>
            )}
            {items.map((row) => (
              <motion.div key={row.friendship_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <FriendRow row={row} onAction={renderRowActions} />
              </motion.div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
